/**
 * Validação e sanitização de valores no frontend.
 * Impede envio de valores negativos ou inválidos à API (ex.: manipulação via DevTools/Cheat Engine).
 */

/** Inteiro dentro do intervalo [min, max]. */
export function clampInt(value, min, max) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

/** Quantidade para carrinho/compra: 1 a 999. */
export function sanitizeQuantity(value) {
  return clampInt(value, 1, 999);
}

/** ID de item/catálogo: inteiro >= 1. */
export function sanitizeItemId(value) {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

/** Valor em BRL para recarga: entre min e max (default 1–2500). */
export function sanitizeAmountBRL(value, min = 1, max = 2500) {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

/** Inteiro positivo, opcionalmente limitado a max. */
export function sanitizePositiveInt(value, defaultVal = 1, max = 999999) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n <= 0) return defaultVal;
  return max != null ? Math.min(max, n) : n;
}

/** Inteiro >= 0. */
export function sanitizeNonNegativeInt(value, defaultVal = 0) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 0) return defaultVal;
  return n;
}

/** Nick/nome do personagem: trim, 4–20 caracteres, apenas a-zA-Z0-9_. */
export function sanitizeNickname(value) {
  if (value == null || typeof value !== 'string') return '';
  const trimmed = value.trim().replace(/[^a-zA-Z0-9_]/g, '');
  return trimmed.slice(0, 20);
}

/** Verifica se o nickname sanitizado tem tamanho válido (4–20). */
export function isNicknameValid(sanitized) {
  return sanitized.length >= 4 && sanitized.length <= 20;
}

/** Estatísticas (str, dex, aim, luck): inteiros não negativos. */
export function sanitizeStats(stats) {
  return {
    str: sanitizeNonNegativeInt(stats?.str, 0),
    dex: sanitizeNonNegativeInt(stats?.dex, 0),
    aim: sanitizeNonNegativeInt(stats?.aim, 0),
    luck: sanitizeNonNegativeInt(stats?.luck, 0)
  };
}

/**
 * Sanitiza um item do carrinho ao carregar do localStorage (anti-manipulação).
 * Garante id, quantity e price dentro de intervalos válidos.
 */
export function sanitizeCartItem(raw, defaults = {}) {
  if (!raw || typeof raw !== 'object') return null;
  const id = sanitizeItemId(raw.id ?? raw.item_id);
  const quantity = sanitizeQuantity(raw.quantity ?? 1);
  const price = Math.max(0, Number(raw.price) ?? 0);
  const name = typeof raw.name === 'string' ? raw.name.slice(0, 200) : (defaults.name || '');
  const icon_url = typeof raw.icon_url === 'string' ? raw.icon_url : (defaults.icon_url || '');
  return { id, quantity, price, name, icon_url };
}

/** Sanitiza array de itens do carrinho (ex.: após JSON.parse do localStorage). */
export function sanitizeCartItems(rawItems, key = 'mixmall') {
  if (!Array.isArray(rawItems)) return [];
  const max = 100;
  return rawItems
    .slice(0, max)
    .map(item => sanitizeCartItem(item))
    .filter(Boolean);
}
